import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Clock3, RefreshCw, TrendingUp, Users2, FileSpreadsheet } from "lucide-react";
import ImportButton from "@/components/ImportButton";

function fmtDur(seconds) {
  const s = Math.max(0, Math.floor(seconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const rem = s % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${m}m ${String(rem).padStart(2, "0")}s`;
  return `${rem}s`;
}
function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
}
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short" });
  } catch { return iso; }
}

const ROLE_COLORS = {
  admin: "bg-red-100 text-red-700 border-red-200",
  branch: "bg-indigo-100 text-indigo-700 border-indigo-200",
  manager: "bg-amber-100 text-amber-700 border-amber-200",
  field_executive: "bg-emerald-100 text-emerald-700 border-emerald-200",
  staff: "bg-slate-100 text-slate-700 border-slate-200",
};

const todayIST = () => {
  const t = new Date();
  const ist = new Date(t.getTime() + (330 - t.getTimezoneOffset()) * 60 * 1000);
  return ist.toISOString().slice(0, 10);
};

export default function LoginHours() {
  const { user } = useAuth();
  const today = todayIST();
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [roleFilter, setRoleFilter] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const isToday = dateFrom === today && dateTo === today;
  const applyToday = () => { setDateFrom(today); setDateTo(today); };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/login-hours", {
        params: { date_from: dateFrom, date_to: dateTo },
      });
      setRows(data?.rows || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateFrom, dateTo]);

  useEffect(() => {
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [dateFrom, dateTo]);

  const filtered = useMemo(() => {
    if (roleFilter === "all") return rows;
    return rows.filter((r) => r.user_role === roleFilter);
  }, [rows, roleFilter]);

  const totals = useMemo(() => {
    const sec = filtered.reduce((a, r) => a + (r.total_seconds || 0), 0);
    const online = filtered.filter((r) => r.still_online).length;
    const todaySec = filtered.filter((r) => r.date === today).reduce((a, r) => a + (r.total_seconds || 0), 0);
    return { seconds: sec, users: filtered.length, online, todaySec };
  }, [filtered, today]);

  const roles = ["all", "admin", "branch", "manager", "field_executive", "staff"];

  return (
    <div className="space-y-4" data-testid="login-hours-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-black text-3xl tracking-tight text-slate-900">Login Hours</h1>
          <p className="text-xs text-slate-500 mt-0.5">Daily login duration for all users. Auto-refreshes every 30 seconds.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={applyToday}
            data-testid="login-hours-today"
            className={`text-[11px] uppercase tracking-wider font-bold px-3 py-1.5 rounded-md border transition ${
              isToday
                ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                : "bg-white text-slate-700 border-slate-200 hover:bg-emerald-50 hover:text-emerald-700"
            }`}
            title="Show only today's login hours"
          >
            Today
          </button>
          <div className="flex items-center gap-1.5 text-xs">
            <span className="text-slate-500 font-semibold">From</span>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 w-36 text-xs" data-testid="login-hours-from" />
            <span className="text-slate-500 font-semibold">To</span>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 w-36 text-xs" data-testid="login-hours-to" />
          </div>
          <Button
            onClick={async () => {
              const res = await api.get("/export/login-hours", { params: { date_from: dateFrom, date_to: dateTo }, responseType: "blob" });
              const url = window.URL.createObjectURL(new Blob([res.data]));
              const a = document.createElement("a");
              a.href = url; a.download = `login-hours-${dateFrom}_to_${dateTo}.xlsx`;
              document.body.appendChild(a); a.click(); a.remove();
              window.URL.revokeObjectURL(url);
            }}
            className="h-8 bg-[color:var(--brand-teal)] hover:bg-[color:var(--brand-teal-hover)] text-white text-xs font-semibold"
            data-testid="login-hours-excel"
          >
            <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" /> Excel
          </Button>
          {user?.role === "admin" && (
            <Button
              onClick={async () => {
                const res = await api.get("/export/login-hours?all=true", { responseType: "blob" });
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement("a");
                a.href = url; a.download = `login-hours-ALL.xlsx`;
                document.body.appendChild(a); a.click(); a.remove();
                window.URL.revokeObjectURL(url);
              }}
              className="h-8 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold"
              data-testid="login-hours-excel-all"
              title="Admin: download every historical login-hour record in one click"
            >
              <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" /> Export ALL
            </Button>
          )}
          {user?.role === "admin" && <ImportButton resource="attendance" onDone={load} label="Import Excel" />}
          <Button onClick={load} disabled={loading} className="h-8 bg-[#2E77E6] hover:bg-[#2361C4] text-white text-xs font-semibold" data-testid="login-hours-refresh">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="rounded-md bg-white border border-slate-200 flex shadow-sm">
          <div className="w-14 shrink-0 flex items-center justify-center bg-[color:var(--brand-teal)]"><Clock3 className="h-6 w-6 text-white" /></div>
          <div className="flex-1 px-4 py-3">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{isToday ? "Total Time Today" : "Total Time"}</div>
            <div className="font-display font-black text-2xl tracking-tight text-slate-900 font-mono-num mt-0.5">{fmtDur(totals.seconds)}</div>
          </div>
        </div>
        <div className="rounded-md bg-white border border-emerald-200 flex shadow-sm">
          <div className="w-14 shrink-0 flex items-center justify-center bg-emerald-600"><Clock3 className="h-6 w-6 text-white" /></div>
          <div className="flex-1 px-4 py-3">
            <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-semibold">Only Today</div>
            <div className="font-display font-black text-2xl tracking-tight text-emerald-700 font-mono-num mt-0.5" data-testid="today-total">{fmtDur(totals.todaySec)}</div>
          </div>
        </div>
        <div className="rounded-md bg-white border border-slate-200 flex shadow-sm">
          <div className="w-14 shrink-0 flex items-center justify-center bg-[#2E77E6]"><Users2 className="h-6 w-6 text-white" /></div>
          <div className="flex-1 px-4 py-3">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Users Logged In</div>
            <div className="font-display font-black text-2xl tracking-tight text-slate-900 font-mono-num mt-0.5">{totals.users}</div>
          </div>
        </div>
        <div className="rounded-md bg-white border border-slate-200 flex shadow-sm">
          <div className="w-14 shrink-0 flex items-center justify-center bg-emerald-500"><TrendingUp className="h-6 w-6 text-white" /></div>
          <div className="flex-1 px-4 py-3">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Currently Online</div>
            <div className="font-display font-black text-2xl tracking-tight text-emerald-700 font-mono-num mt-0.5">{totals.online}</div>
          </div>
        </div>
      </div>

      {/* Role filter tabs */}
      <div className="flex items-center gap-2 flex-wrap">
        {roles.map((r) => (
          <button
            key={r}
            onClick={() => setRoleFilter(r)}
            data-testid={`role-filter-${r}`}
            className={`text-[11px] uppercase tracking-wider font-bold px-3 py-1.5 rounded-md border transition ${
              roleFilter === r
                ? "bg-[color:var(--brand-navy)] text-white border-[color:var(--brand-navy)]"
                : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
            }`}
          >
            {r === "all" ? "All Roles" : r.replace("_", " ")}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm crm-table" data-testid="login-hours-table">
            <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2.5 text-left font-semibold">Date</th>
                <th className="px-3 py-2.5 text-left font-semibold">User</th>
                <th className="px-3 py-2.5 text-left font-semibold">Role</th>
                <th className="px-3 py-2.5 text-left font-semibold">Email</th>
                <th className="px-3 py-2.5 text-right font-semibold">Sessions</th>
                <th className="px-3 py-2.5 text-right font-semibold">First Login</th>
                <th className="px-3 py-2.5 text-right font-semibold">Last Active</th>
                <th className="px-3 py-2.5 text-right font-semibold">Total Time</th>
                <th className="px-3 py-2.5 text-right font-semibold bg-emerald-50" title="Elapsed today (live for online users)">Today</th>
                <th className="px-3 py-2.5 text-right font-semibold">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.length === 0 ? (
                <tr><td colSpan={10} className="px-3 py-8 text-center text-slate-400">No login activity in the selected range.</td></tr>
              ) : filtered.map((r, idx) => (
                <tr key={r.user_id + r.date + idx}>
                  <td className="px-3 py-2 font-mono-num text-slate-600">{fmtDate(r.date)}</td>
                  <td className="px-3 py-2 font-semibold text-slate-800">{r.user_name || "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${ROLE_COLORS[r.user_role] || ROLE_COLORS.staff}`}>
                      {(r.user_role || "").replace("_", " ") || "—"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-slate-500 text-[12px]">{r.user_email || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono-num text-slate-600">{r.sessions}</td>
                  <td className="px-3 py-2 text-right font-mono-num text-slate-600">{fmtTime(r.first_login)}</td>
                  <td className="px-3 py-2 text-right font-mono-num text-slate-600">{fmtTime(r.last_active)}</td>
                  <td className="px-3 py-2 text-right font-mono-num font-bold text-slate-900">{fmtDur(r.total_seconds)}</td>
                  <td className="px-3 py-2 text-right font-mono-num font-bold bg-emerald-50/30" data-testid={`today-time-${r.user_id}`}>
                    {r.date === today ? (
                      <span className={r.still_online ? "text-emerald-700" : "text-slate-700"}>{fmtDur(r.total_seconds)}</span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {r.still_online ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-200">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> ONLINE
                      </span>
                    ) : (
                      <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-500 border border-slate-200">
                        OFFLINE
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
